import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { useId } from 'react'

/**
 * `input-label` — 24 screens (design-spec.md §5.4).
 *
 * A vertical stack with a 10px gap: a 12/Medium/white-80 right-aligned label,
 * then a 60-high radius-16 field on `white/5%` with a `white/16%` border and
 * an 8px backdrop blur. The border is the Figma variable `color/white/ 15_7%`
 * — §5.4's "8%" is the card/tab-bar value, not the input one.
 *
 * The value is 16px on the auth and identity screens and 15px on the address
 * screen; `size` carries that difference rather than hiding it.
 */
type FieldSize = 'md' | 'sm'

interface CommonProps {
  label?: string
  error?: string | null
  hint?: string
  size?: FieldSize
  className?: string
}

interface FieldProps
  extends CommonProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'> {
  /** Rendered inside the field, before the input — e.g. the country chip. */
  leading?: ReactNode
  /** Rendered inside the field, after the input — e.g. a "max" action. */
  trailing?: ReactNode
  /** Numeric/latin content reads LTR inside the RTL line (design-spec.md §7). */
  ltr?: boolean
}

const VALUE_SIZE: Record<FieldSize, string> = { md: 'text-16', sm: 'text-15' }

function shell(error: string | null | undefined, extra = '') {
  return [
    'flex rounded-16 border bg-surface-5 backdrop-blur-[8px]',
    'transition-colors duration-(--sx-dur)',
    error ? 'border-danger' : 'border-line-16 focus-within:border-primary-glow',
    extra,
  ].join(' ')
}

/** 19px label box: §5.4's stack is 19 + 10 gap + 60 field = 89. */
function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-12 leading-[19px] font-medium text-ink-80">
      {children}
    </label>
  )
}

function Footnote({ error, hint }: { error?: string | null; hint?: string }) {
  if (error) {
    return (
      <p role="alert" className="text-12 leading-[19px] text-danger">
        {error}
      </p>
    )
  }
  if (hint) return <p className="text-12 leading-[19px] text-ink-50">{hint}</p>
  return null
}

export function Field({
  label,
  leading,
  trailing,
  error,
  hint,
  ltr = false,
  size = 'md',
  className = '',
  id,
  ...rest
}: FieldProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId

  return (
    <div className="flex w-full flex-col gap-[10px]">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div dir={ltr ? 'ltr' : undefined} className={shell(error, `h-field items-center gap-2 ps-2 pe-4 ${className}`)}>
        {leading}
        <input
          id={inputId}
          className={`sx-num min-w-0 flex-1 ${VALUE_SIZE[size]} text-ink placeholder:text-ink-38 ${
            ltr ? 'text-left' : 'text-right'
          }`}
          {...rest}
        />
        {trailing}
      </div>
      <Footnote error={error} hint={hint} />
    </div>
  )
}

interface TextFieldProps
  extends CommonProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {}

/**
 * The multi-line variant — the full address on screen 16 is an 80-high field
 * with 12px padding instead of the 60-high single-line one.
 */
export function TextField({
  label,
  error,
  hint,
  size = 'sm',
  className = '',
  id,
  ...rest
}: TextFieldProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId

  return (
    <div className="flex w-full flex-col gap-[10px]">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      <div className={shell(error, `h-[80px] p-3 ${className}`)}>
        <textarea
          id={inputId}
          className={`w-full resize-none ${VALUE_SIZE[size]} leading-[19px] text-ink placeholder:text-ink-38`}
          {...rest}
        />
      </div>
      <Footnote error={error} hint={hint} />
    </div>
  )
}
